// internationalization
import { useTranslation } from 'react-i18next';

// components
import { formatNumber } from '@/utils/formatters';

// relative
import type { BenchmarkChartData } from './benchmark';

import { useTheme } from '@emotion/react';
import Highcharts from 'highcharts';
import type { Options } from 'highcharts';

/** Compute histogram bins for values within [min, max], using square-root bin count. */
const computeBins = (
  values: number[],
  min: number,
  max: number,
): { data: [number, number][]; binWidth: number } => {
  const visible = values.filter((v) => v >= min && v <= max);
  const n = visible.length;
  if (n === 0) return { data: [], binWidth: 1 };
  const range = max - min || 1;
  const binCount = Math.max(5, Math.min(30, Math.ceil(Math.sqrt(n))));
  const bw = range / binCount;
  const counts = new Array<number>(binCount).fill(0);
  visible.forEach((v) => {
    const idx = Math.min(Math.floor((v - min) / bw), binCount - 1);
    counts[idx]++;
  });
  return {
    data: counts.map(
      (count, i) => [min + (i + 0.5) * bw, count] as [number, number],
    ),
    binWidth: bw,
  };
};

interface UseBenchmarkChartOptionsResult {
  options: Options;
}

const useBenchmarkChartOptions = (
  data: Partial<BenchmarkChartData>,
): UseBenchmarkChartOptionsResult => {
  const theme = useTheme();
  const { t } = useTranslation('portfolio-analysis');
  const isDark = (theme as Record<string, unknown>).palette
    ? (theme as { palette: { mode: string } }).palette.mode === 'dark'
    : false;

  const rawValues = data.BenchmarkHistogramValues;
  const histogramBins = data.BenchmarkHistogram ?? [];

  // Initial bins — full range
  let columnData: [number, number][];
  let binWidth: number;

  if (rawValues && rawValues.length > 0) {
    const min = Math.min(...rawValues);
    const max = Math.max(...rawValues);
    const initial = computeBins(rawValues, min, max);
    columnData = initial.data;
    binWidth = initial.binWidth;
  } else {
    // Fall back to pre-computed bins from backend
    columnData = histogramBins.map((bin) => [
      (bin.BinStart + bin.BinEnd) / 2,
      bin.Count,
    ]);
    binWidth =
      histogramBins.length > 0
        ? histogramBins[0].BinEnd - histogramBins[0].BinStart
        : 1;
  }

  const buildingValue = data.BenchmarkData?.kWhPrYPrM2 ?? 0;
  const medianValue = data.BenchmarkData?.GroupMedian ?? 0;
  const textColor = isDark ? 'white' : 'black';

  // Max count from initial bins — yAxis auto-scales after zoom recomputes

  const options: Options = {
    chart: {
      type: 'column',
      zooming: {
        type: 'x',
        mouseWheel: { enabled: false },
      },
      backgroundColor: 'transparent',
      spacingTop: 30,
      events: {
        selection(e) {
          if (!rawValues || rawValues.length === 0) return;
          const chart = this as Highcharts.Chart;
          const allMin = Math.min(...rawValues);
          const allMax = Math.max(...rawValues);

          if ((e as unknown as { resetSelection?: boolean }).resetSelection) {
            // Restore full-range bins
            const { data: fullData, binWidth: fullBinWidth } = computeBins(
              rawValues,
              allMin,
              allMax,
            );
            chart.series[0].update(
              { pointRange: fullBinWidth } as Highcharts.SeriesColumnOptions,
              false,
            );
            chart.series[0].setData(
              fullData as Highcharts.PointOptionsType[],
              false,
            );
            // Pad axis by half a bin so the outermost bars aren't clipped
            chart.xAxis[0].setExtremes(
              allMin,
              allMax + fullBinWidth * 0.5,
              true,
            );
            // Hide the reset button manually (Highcharts won't since we return false)
            const resetBtn = (
              chart as unknown as { resetZoomButton?: { destroy: () => void } }
            ).resetZoomButton;
            if (resetBtn) {
              resetBtn.destroy();
              (
                chart as unknown as { resetZoomButton?: unknown }
              ).resetZoomButton = undefined;
            }
            return false;
          }

          // User drag-zoom selection
          const xSel = (
            e as unknown as { xAxis?: { min: number; max: number }[] }
          ).xAxis?.[0];
          if (!xSel) return;
          const { data: newData, binWidth: newBinWidth } = computeBins(
            rawValues,
            xSel.min,
            xSel.max,
          );
          chart.series[0].update(
            { pointRange: newBinWidth } as Highcharts.SeriesColumnOptions,
            false,
          );
          chart.series[0].setData(
            newData as Highcharts.PointOptionsType[],
            false,
          );
          // Pad axis so the rightmost and leftmost bars are fully visible
          chart.xAxis[0].setExtremes(
            xSel.min - newBinWidth * 0.5,
            xSel.max + newBinWidth * 0.5,
            true,
          );
          (chart as unknown as { showResetZoom: () => void }).showResetZoom();
          return false;
        },
      },
    },
    boost: { enabled: false },
    title: { text: '' },
    xAxis: {
      title: { text: t('benchmarkChart.chart.xAxis') },
      plotLines: [
        {
          color: 'hsl(210, 80%, 50%)',
          dashStyle: 'Dash',
          width: 2,
          value: medianValue,
          zIndex: 5,
          label: {
            text: `${t('benchmarkChart.chart.medianLabel')} (${formatNumber(medianValue)})`,
            style: { color: 'hsl(210, 80%, 50%)', fontWeight: 'bold' },
            rotation: 0,
            y: 15,
            verticalAlign: 'top',
          },
        },
        {
          color: 'hsl(0, 80%, 50%)',
          dashStyle: 'Dash',
          width: 2,
          value: buildingValue,
          zIndex: 5,
          label: {
            text: `${t('benchmarkChart.chart.yourBuildingLabel')} (${formatNumber(buildingValue)})`,
            style: { color: 'hsl(0, 80%, 50%)', fontWeight: 'bold' },
            rotation: 0,
            y: 30,
            verticalAlign: 'top',
            align: 'left',
            x: 4,
          },
        },
      ],
      labels: { style: { color: textColor } },
      minRange: 10,
      startOnTick: false,
      endOnTick: false,
      // Pad by half a bin on each side so outermost bars aren't clipped
      min:
        columnData.length > 0 ? columnData[0][0] - binWidth * 0.5 : undefined,
      max:
        columnData.length > 0
          ? columnData[columnData.length - 1][0] + binWidth * 0.5
          : undefined,
    },
    yAxis: {
      title: { text: t('benchmarkChart.chart.yAxis') },
      labels: { style: { color: textColor } },
      allowDecimals: false,
      // No fixed max — Highcharts auto-scales after zoom recomputes bins
    },
    tooltip: {
      headerFormat: '',
      pointFormat: `{point.y} ${t('benchmarkChart.kpi.buildingsUnit')}`,
    },
    plotOptions: {
      column: {
        pointWidth: undefined,
        groupPadding: 0,
        pointPadding: 0,
        borderWidth: 1,
        borderColor: isDark ? 'hsl(63, 80%, 30%)' : 'hsl(63, 100%, 30%)',
      },
    },
    series: [
      {
        name: t('benchmarkChart.chart.distributionLabel'),
        data: columnData,
        color: 'hsl(63, 100%, 40%)',
        type: 'column',
        pointRange: binWidth,
        showInLegend: true,
        enableMouseTracking: true,
      },
      // Invisible series for median legend entry
      {
        name: `${t('benchmarkChart.chart.medianLabel')} - ${formatNumber(medianValue)}`,
        type: 'line',
        data: [],
        color: 'hsl(210, 80%, 50%)',
        dashStyle: 'Dash',
        marker: { enabled: false },
        enableMouseTracking: false,
        showInLegend: true,
      },
      // Invisible series for building legend entry
      {
        name: `${t('benchmarkChart.chart.yourBuildingLabel')} - ${formatNumber(buildingValue)}`,
        type: 'line',
        data: [],
        color: 'hsl(0, 80%, 50%)',
        marker: { enabled: false },
        enableMouseTracking: false,
        showInLegend: true,
      },
    ],
    legend: {
      enabled: true,
      itemStyle: { color: textColor },
    },
  };

  return { options };
};

export default useBenchmarkChartOptions;
